import { SubscriptionType } from '@common/enums/SubscriptionTypeEnum.ts';

export type LandingImage = {
    src: string;
    alt: string;
    width: number;
    height: number;
};

export type HowItWorksStep = {
    title: string;
    body: string;
    image: LandingImage;
};

export type ClosedDeal = {
    image: LandingImage;
    purchasePrice: string;
    rehabCost: string;
    arv: string;
    equityCreated: string;
};

/** A single block of an FAQ answer. `text` may contain {@link PRODUCT_NAME_PLACEHOLDER}. */
export type FaqBlock =
    | { kind: 'paragraph'; text: string }
    | { kind: 'orderedList'; items: string[] }
    | { kind: 'bulletList'; items: string[] };

export type FaqEntry = {
    question: string;
    /** The answer used unless the product's subscription type has an override below. */
    answer: FaqBlock[];
    /** Copy for products whose lead model differs, e.g. prepaid packages rather than a monthly budget. */
    answerOverrides?: Partial<Record<SubscriptionType, FaqBlock[]>>;
};

/** Replaced with the product's display name wherever it appears in FAQ copy. */
export const PRODUCT_NAME_PLACEHOLDER = '{product}';

export const STEP_SEPARATOR_IMAGE: LandingImage = {
    src: '/images/detail-curve.webp&w=640&q=100.webp',
    alt: '',
    width: 300,
    height: 95
};

export const HOW_IT_WORKS_STEPS: HowItWorksStep[] = [
    {
        title: '1. Bid on regions you are targeting',
        body: 'Choose your preferred counties and set your price per lead. We will aggressively advertise in your ' +
            'selected areas using our network of high-quality brands to ensure maximum exposure.',
        image: {
            src: '/images/select-county-modified.png',
            alt: 'Selecting target counties and setting a price per lead',
            width: 528,
            height: 322
        }
    },
    {
        title: '2. Set your monthly budget',
        body: 'You will only be charged as we generate leads for you. We will stop generating leads for you once ' +
            'your budget limit is reached, which resets monthly. Adjust your budget anytime to fit your marketing needs.',
        image: {
            src: '/images/select-budget-modified.png',
            alt: 'Setting a monthly lead budget',
            width: 528,
            height: 313
        }
    },
    {
        title: '3. Configure your lead delivery',
        body: 'Type in the email address and SMS where you would like to receive leads. We also support CRM integration',
        image: {
            src: '/images/contact-info-modified.png',
            alt: 'Entering the email address and phone number that leads are delivered to',
            width: 528,
            height: 342
        }
    },
    {
        title: '4. Start receiving leads',
        body: "Leads arrive in real time, letting you quickly contact sellers. Keep receiving leads as long as your " +
            "account remains active and funds are available. Feel free to pause anytime. We conduct basic quality " +
            "checks but don't pre-vet every lead. However, you can dispute leads that don’t meet specific quality " +
            "criteria.",
        image: {
            src: '/images/receiving-leads.png',
            alt: 'A list of leads as they are received',
            width: 528,
            height: 700
        }
    }
];

export const FAQ_HEADING = 'Frequently Asked Questions';
export const FAQ_LEDE = 'Everything you need to know before your first lead.';

export const FAQ_ENTRIES: FaqEntry[] = [
    {
        question: `What is ${PRODUCT_NAME_PLACEHOLDER}?`,
        answer: [
            {
                kind: 'paragraph',
                text: `${PRODUCT_NAME_PLACEHOLDER} is a pay-per-lead service that connects real estate investors, ` +
                    'wholesalers, and agents with homeowners who have expressed interest in selling their property. ' +
                    'Instead of paying monthly retainers or long-term contracts, you only pay for qualified seller ' +
                    'leads delivered directly to you.'
            }
        ],
        answerOverrides: {
            [SubscriptionType.Static]: [
                {
                    kind: 'paragraph',
                    text: `${PRODUCT_NAME_PLACEHOLDER} is a lead service that connects real estate investors, ` +
                        'wholesalers, and agents with homeowners who have expressed interest in selling their ' +
                        'property. You buy a package of leads up front — no monthly retainers, no long-term ' +
                        'contracts — and we deliver qualified seller leads until your package is used.'
                }
            ]
        }
    },
    {
        question: 'How are seller leads generated?',
        answer: [
            {
                kind: 'paragraph',
                text: 'We generate leads through a combination of digital marketing strategies, including social ' +
                    'media advertising, search engine marketing, online lead forms, and targeted campaigns designed ' +
                    'to reach motivated homeowners. Every lead comes from a homeowner who has voluntarily submitted ' +
                    'their information requesting to discuss selling their property.'
            }
        ]
    },
    {
        question: 'Do you offer exclusive leads?',
        answer: [
            {
                kind: 'paragraph',
                text: 'Yes. Our standard leads are exclusive and sold to only one client in the selected market. ' +
                    'Once a lead is assigned to you, it is not resold to another customer.'
            }
        ]
    },
    {
        question: 'What is the lead refund policy?',
        answer: [
            {
                kind: 'paragraph',
                text: 'We stand behind the quality of our leads. If a lead does not meet our qualification ' +
                    'standards, you may submit it for review within the specified review period. Eligible leads ' +
                    'that meet our refund criteria will be credited back to your account. Common qualifying reasons ' +
                    'may include invalid contact information, duplicate leads, or leads outside your selected ' +
                    'market. We encourage clients to contact leads promptly, as response time significantly impacts ' +
                    'conversion rates.'
            }
        ],
        answerOverrides: {
            [SubscriptionType.Static]: [
                {
                    kind: 'paragraph',
                    text: 'We stand behind the quality of our leads. If a lead does not meet our qualification ' +
                        'standards, you may submit it for review within the specified review period. Eligible leads ' +
                        'that meet our refund criteria are returned to your lead balance, so they do not count ' +
                        'against your package. Common qualifying reasons may include invalid contact information, ' +
                        'duplicate leads, or leads outside your selected market. We encourage clients to contact ' +
                        'leads promptly, as response time significantly impacts conversion rates.'
                }
            ]
        }
    },
    {
        question: 'How do I get started?',
        answer: [
            { kind: 'paragraph', text: 'Getting started is simple:' },
            {
                kind: 'orderedList',
                items: [
                    'Schedule a quick onboarding call.',
                    'Select your target market and buying criteria.',
                    'Fund your lead account and set your monthly budget.',
                    'Start receiving seller leads as they are generated.'
                ]
            },
            {
                kind: 'paragraph',
                text: 'There are no long-term contracts — you simply pay for the leads you receive.'
            }
        ],
        answerOverrides: {
            [SubscriptionType.Static]: [
                { kind: 'paragraph', text: 'Getting started is simple:' },
                {
                    kind: 'orderedList',
                    items: [
                        'Schedule a quick onboarding call.',
                        'Select your target market and buying criteria.',
                        'Choose the lead package that fits your volume.',
                        'Start receiving seller leads as they are generated.'
                    ]
                },
                {
                    kind: 'paragraph',
                    text: 'There are no long-term contracts — you buy leads in the quantity that suits you, and ' +
                        'unused leads carry over.'
                }
            ]
        }
    },
    {
        question: 'How are seller leads delivered?',
        answer: [
            {
                kind: 'paragraph',
                text: 'Leads are delivered in real time via text message, email, and through your client dashboard, ' +
                    'allowing you to contact homeowners as quickly as possible while their interest is highest.'
            }
        ]
    },
    {
        question: 'How do you qualify the leads?',
        answer: [
            {
                kind: 'paragraph',
                text: 'Each lead is reviewed before delivery. Our qualification process includes verifying contact ' +
                    'information and gathering key details such as:'
            },
            {
                kind: 'bulletList',
                items: [
                    'Property address',
                    'Property type',
                    'Ownership status',
                    'Condition of the property',
                    'Seller motivation',
                    'Desired timeline to sell',
                    'Equity or mortgage information when available'
                ]
            },
            {
                kind: 'paragraph',
                text: "The amount of information collected may vary depending on the homeowner's responses."
            }
        ]
    },
    {
        question: 'Can I get a trial period or sample leads?',
        answer: [
            {
                kind: 'paragraph',
                text: 'Rather than offering free sample leads, we allow new clients to start with a small initial ' +
                    'lead purchase so they can evaluate the quality of our service with minimal risk. This allows ' +
                    'you to experience our process firsthand before increasing your lead volume.'
            }
        ],
        answerOverrides: {
            [SubscriptionType.Static]: [
                {
                    kind: 'paragraph',
                    text: 'Rather than offering free sample leads, we allow new clients to start with our smallest ' +
                        'lead package so they can evaluate the quality of our service with minimal risk. This ' +
                        'allows you to experience our process firsthand before moving up to a larger package.'
                }
            ]
        }
    }
];

export const RECENTLY_CLOSED_DEALS: ClosedDeal[] = [
    {
        image: { src: '/images/house1.jpeg', alt: '', width: 350, height: 220 },
        purchasePrice: '$425,000',
        rehabCost: '$40,000',
        arv: '$575,000',
        equityCreated: '$110,000'
    },
    {
        image: { src: '/images/house2.jpeg', alt: '', width: 350, height: 220 },
        purchasePrice: '$280,000',
        rehabCost: '$57,750',
        arv: '$420,000',
        equityCreated: '$82,250'
    },
    {
        image: { src: '/images/house3.jpeg', alt: '', width: 350, height: 220 },
        purchasePrice: '$600,000',
        rehabCost: '$140,000',
        arv: '$790,000',
        equityCreated: '$50,000'
    }
];
